import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type {
  StockData,
  ChartDataPoint,
  ProcessedFinancialMetricsPayload,
  NewsEvent
} from '../types/game';
import type { StockCategoryFilter } from '../types/stockCategories';
import { getDailyStockForCategory } from '../utils/categoryStockSelection';

interface UseCategoryStockDataReturn {
  stockData: StockData | null;
  chartData: ChartDataPoint[];
  processedFinancialMetrics: ProcessedFinancialMetricsPayload | null;
  newsEvents: NewsEvent[] | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  category: StockCategoryFilter;
  source: 'cache' | 'api' | null;
  debugChartData: () => Record<string, unknown>;
}

interface UseCategoryStockDataOptions {
  category: StockCategoryFilter;
  autoFetch?: boolean;
  date?: string;
}

const useQuerySource = (
  hasData: boolean,
  isFetching: boolean
): 'cache' | 'api' | null => {
  if (!hasData) {
    return null;
  }

  return isFetching ? 'api' : 'cache';
};

export function useCategoryStockData(
  options: UseCategoryStockDataOptions
): UseCategoryStockDataReturn {
  const { category, autoFetch = true, date } = options;

  const queryKey = useMemo(
    () => ['daily-stock', category, date ?? 'current'],
    [category, date]
  );

  const query = useQuery({
    queryKey,
    queryFn: () => getDailyStockForCategory(category, date),
    enabled: autoFetch,
    staleTime: 10_000,
    gcTime: 5 * 60 * 1000
  });

  const stockData = query.data?.stock ?? null;
  const chartData = query.data?.chartData ?? [];
  const processedFinancialMetrics = query.data?.processedFinancialMetrics ?? null;
  const newsEvents = query.data?.newsEvents ?? null;

  const errorMessage = useMemo(() => {
    if (!query.error) {
      return null;
    }

    if (query.error instanceof Error) {
      return query.error.message;
    }

    return 'Failed to load stock data';
  }, [query.error]);

  const refetch = useCallback(async () => {
    const result = await query.refetch({ throwOnError: true });
    if (result.error) {
      throw result.error;
    }
  }, [query]);

  const source = useQuerySource(Boolean(query.data), query.isFetching);

  const debugChartData = useCallback(() => {
    return {
      category,
      queryKey,
      source,
      hasData: Boolean(stockData),
      stockSymbol: stockData?.Symbol,
      chartDataLength: chartData.length,
      isFetching: query.isFetching,
      isLoading: query.isLoading,
      isStale: query.isStale,
      error: errorMessage
    };
  }, [category, chartData.length, errorMessage, query.isFetching, query.isLoading, query.isStale, queryKey, source, stockData]);

  return {
    stockData,
    chartData,
    processedFinancialMetrics,
    newsEvents,
    isLoading: query.isLoading,
    error: errorMessage,
    refetch,
    category,
    source,
    debugChartData
  };
}

export default useCategoryStockData;
